export const MOD_SHIFT = 1 << 0;
export const MOD_CTRL  = 1 << 1;
export const MOD_ALT   = 1 << 2;
export const MOD_META  = 1 << 3;

export interface CanvasGeom {
  width: number;
  height: number;
}

export interface MouseSource {
  clientX: number;
  clientY: number;
  buttons?: number;
  button?: number;
}

export interface KeySource {
  code: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

export interface ModSource {
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

export interface MouseMovePayload { kind: 'move'; x: number; y: number; buttons: number; ts: number; }
export interface MouseButtonPayload { kind: 'button'; x: number; y: number; button: number; down: boolean; ts: number; }
export interface MouseWheelPayload { kind: 'wheel'; deltaX: number; deltaY: number; ts: number; }
export interface KeyPayload { keyCode: string; down: boolean; modifiers: number; ts: number; }

export function normalizePoint(value: number, min: number, max: number): number {
  if (max <= min) return 0;
  const ratio = (value - min) / (max - min);
  if (ratio < 0) return 0;
  if (ratio > 1) return 1;
  return ratio;
}

export function modifiersFromEvent(e: ModSource): number {
  let bits = 0;
  if (e.shiftKey) bits |= MOD_SHIFT;
  if (e.ctrlKey)  bits |= MOD_CTRL;
  if (e.altKey)   bits |= MOD_ALT;
  if (e.metaKey)  bits |= MOD_META;
  return bits;
}

export function encodeMouseMove(src: MouseSource, geom: CanvasGeom, ts: number): MouseMovePayload {
  return {
    kind: 'move',
    x: normalizePoint(src.clientX, 0, geom.width),
    y: normalizePoint(src.clientY, 0, geom.height),
    buttons: src.buttons ?? 0,
    ts,
  };
}

export function encodeMouseButton(src: MouseSource, geom: CanvasGeom, down: boolean, ts: number): MouseButtonPayload {
  return {
    kind: 'button',
    x: normalizePoint(src.clientX, 0, geom.width),
    y: normalizePoint(src.clientY, 0, geom.height),
    button: src.button ?? 0,
    down,
    ts,
  };
}

export function encodeMouseWheel(src: { deltaX: number; deltaY: number }, ts: number): MouseWheelPayload {
  return { kind: 'wheel', deltaX: src.deltaX, deltaY: src.deltaY, ts };
}

export function encodeKey(src: KeySource, down: boolean, ts: number): KeyPayload {
  return { keyCode: src.code, down, modifiers: modifiersFromEvent(src), ts };
}

export function makeMoveThrottle(intervalMs: number): { tick: () => boolean } {
  let last = 0;
  return {
    tick() {
      const now = performance.now();
      if (now - last >= intervalMs) { last = now; return true; }
      return false;
    },
  };
}
